import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { decodeText } from '@aytm/app/stats/lib/helpers'

import { onInputKeyDown } from '@aytm/app/stats/page/sidebar/components/lib/utils'

import { InputTextarea } from 'primereact/inputtextarea'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

export const DataContext = React.createContext()

const InputUpdate = ({
  value,
  maxLength,
  forbiddenSymbols,
  isEditing,
  isProcessing,
  onChange,
  onConfirm,
  onCancel
}) => {
  const [inputValue, setInputValue] = useState(decodeText(value, false))

  const callbackRef = useRef(null)

  const onInputKeyUp = event => {
    if (13 == event.keyCode) onConfirm(inputValue)
    if (27 == event.keyCode) onCancel()
  }

  const onInputChange = event => setInputValue(event.target.value)

  const onInputPaste = useCallback(
    event => {
      event.preventDefault()

      let text =
        (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
      text = text
        .replace(new RegExp(`[${forbiddenSymbols}]`, 'gi'), '')
        .replace(/(\r\n|\n|\r|\t|\v)/gm, ' ')
        .trim()

      const el = event.currentTarget

      const l = text.length
      const start = el.selectionStart
      const end = el.selectionEnd

      const valueBefore = el.value.slice(0, start)
      const valueAfter = el.value.slice(end)

      text = `${valueBefore}${text}${valueAfter}`.slice(0, maxLength)

      setInputValue(text)
      callbackRef.current = () => {
        el.setSelectionRange(start + l, start + l)
        el.blur()
        el.focus()
      }
    },
    [maxLength, forbiddenSymbols]
  )

  useEffect(() => {
    if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
      callbackRef.current()
      callbackRef.current = null
    }
  }, [inputValue])

  useEffect(() => {
    onChange(inputValue)
  }, [inputValue, onChange])

  useEffect(() => {
    !isEditing && setInputValue(decodeText(value, false))
  }, [value, isEditing])

  return isEditing ? (
    <div className={style['component-textarea']}>
      <InputTextarea
        className="p-composite"
        value={inputValue}
        maxLength={maxLength}
        keyfilter={new RegExp(`^[^${forbiddenSymbols}]*$`, 'i')}
        rows={1}
        autoFocus
        autoResize
        disabled={isProcessing}
        onKeyDown={isProcessing ? null : onInputKeyDown}
        onKeyUp={isProcessing ? null : onInputKeyUp}
        onChange={isProcessing ? null : onInputChange}
        onPaste={isProcessing ? null : onInputPaste}
      />
      <span className={style['component-textarea-count']}>{maxLength - inputValue.length}</span>
    </div>
  ) : (
    <span dangerouslySetInnerHTML={{ __html: value }} />
  )
}

InputUpdate.propTypes = {
  value: PropTypes.string,
  maxLength: PropTypes.number,
  forbiddenSymbols: PropTypes.string,
  isEditing: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onChange: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func
}

InputUpdate.displayName = 'InputUpdate'

export default InputUpdate
